// CSR-2063 — the ACORD form as the agent edits it.
//
// The page itself is a canvas: pdf.js draws the blank paper, exactly as it prints.
// Over each fill-in blank sits a real HTML input, positioned from the blank's own
// rectangle in the PDF. So the agent sees the form and edits the form, but the
// values live in React state — the finished document is produced server-side,
// which is why the overlay is deliberately not WYSIWYG: the server owns font
// fitting (CSR-2058), so a long value looks cramped here and comes out shrunk to
// fit there.
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { acordBlankBaseName } from "@shared/acord-forms";
import type { AcordFormValues } from "@/lib/api";
import type { AcordPdfPage } from "./acord-pdf-document";
import type { AcordPdfField } from "./acord-pdf-fields";

interface Props {
  pages: AcordPdfPage[];
  values: AcordFormValues;
  onTextChange: (name: string, value: string) => void;
  onCheckChange: (name: string, checked: boolean) => void;
  onRadioChange: (name: string, option: string) => void;
  // True while saving — the form stays visible and in place, it just stops taking
  // edits.
  disabled?: boolean;
}

// Room for the scrollbar and the page's drop shadow.
const HORIZONTAL_GUTTER = 32;
const ZOOM_STEP = 0.2;
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2.5;
// Below this a blank is too small to click; above it the page stops fitting any
// screen we care about.
const MIN_SCALE = 0.3;
const MAX_SCALE = 3;
// Fallback until the container has been measured (tests, and the first paint).
const DEFAULT_CONTAINER_WIDTH = 820;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// `F[0].P1[0].NamedInsured_FullName_A[0]` reads as "NamedInsured FullName A" —
// the only human-readable name a PDF blank has.
export function acordFieldLabel(name: string): string {
  return acordBlankBaseName(name).replace(/_/g, " ").trim();
}

export function AcordPdfForm({
  pages,
  values,
  onTextChange,
  onCheckChange,
  onRadioChange,
  disabled = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());
  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONTAINER_WIDTH);
  const [zoom, setZoom] = useState(1);

  // Fit the page to the panel, then let the agent zoom from there — an ACORD page
  // is wider than a phone, and a fixed scale would either overflow it or waste
  // half a desktop screen.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => setContainerWidth(container.clientWidth || DEFAULT_CONTAINER_WIDTH);
    measure();

    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const pageWidth = pages[0]?.width ?? 0;
  const fitScale = pageWidth > 0 ? (containerWidth - HORIZONTAL_GUTTER) / pageWidth : 1;
  const scale = clamp(fitScale * zoom, MIN_SCALE, MAX_SCALE);

  const setCanvasRef = useCallback((pageNumber: number, canvas: HTMLCanvasElement | null) => {
    if (canvas) canvasRefs.current.set(pageNumber, canvas);
    else canvasRefs.current.delete(pageNumber);
  }, []);

  useEffect(() => {
    if (pages.length === 0) return;
    const tasks: { cancel: () => void }[] = [];

    for (const { pageNumber, page } of pages) {
      const canvas = canvasRefs.current.get(pageNumber);
      const context = canvas?.getContext("2d");
      // happy-dom has no 2D context; the overlay is still worth rendering, and in
      // a browser this only skips a page that has already unmounted.
      if (!canvas || !context) continue;

      const viewport = page.getViewport({ scale });
      // Without the device pixel ratio the canvas is upscaled by the browser and
      // the printed form turns to mush on any retina screen.
      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      context.setTransform(outputScale, 0, 0, outputScale, 0, 0);

      const task = page.render({ canvasContext: context, viewport, canvas });
      tasks.push(task);
      task.promise.catch((error: unknown) => {
        // A cancelled render is the expected outcome of a zoom mid-draw.
        if ((error as { name?: string })?.name === "RenderingCancelledException") return;
        console.error("ACORD page render failed:", error);
      });
    }

    return () => tasks.forEach((task) => task.cancel());
  }, [pages, scale]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="acord-pdf-form">
      <div className="flex shrink-0 items-center justify-center gap-2 border-b border-border/30 bg-muted/30 px-4 py-2">
        {/* Not `acord-page-…`: that prefix belongs to the pages themselves, and a
            test selecting them all should not pick up this label. */}
        <span className="mr-auto text-xs text-muted-foreground" data-testid="acord-form-page-count">
          {pages.length} {pages.length === 1 ? "page" : "pages"}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 cursor-pointer"
          onClick={() => setZoom((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP))}
          aria-label="Zoom out"
          data-testid="button-acord-zoom-out"
        >
          <ZoomOut className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="w-[44px] text-center text-xs text-muted-foreground">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 cursor-pointer"
          onClick={() => setZoom((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP))}
          aria-label="Zoom in"
          data-testid="button-acord-zoom-in"
        >
          <ZoomIn className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-auto bg-neutral-100 p-4 dark:bg-neutral-900"
      >
        <div className="flex flex-col items-center gap-4">
          {pages.map((page) => (
            <div
              key={page.pageNumber}
              className="relative bg-white shadow-sm"
              style={{
                width: `${Math.floor(page.width * scale)}px`,
                height: `${Math.floor(page.height * scale)}px`,
              }}
              data-testid={`acord-page-${page.pageNumber}`}
            >
              <canvas
                ref={(canvas) => setCanvasRef(page.pageNumber, canvas)}
                className="block"
                aria-hidden="true"
              />
              <div className="absolute inset-0">
                {page.fields.map((field) => (
                  <AcordFieldInput
                    key={field.key}
                    field={field}
                    scale={scale}
                    // Scalars, not the whole value set: ACORD 127 has 634 blanks,
                    // and passing the set would re-render every one of them on
                    // every keystroke.
                    text={values.text[field.name] ?? ""}
                    checked={
                      field.kind === "radio"
                        ? values.radios[field.name] === field.option
                        : values.checks[field.name] === true
                    }
                    disabled={disabled}
                    onTextChange={onTextChange}
                    onCheckChange={onCheckChange}
                    onRadioChange={onRadioChange}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const AcordFieldInput = memo(function AcordFieldInput({
  field,
  scale,
  text,
  checked,
  disabled,
  onTextChange,
  onCheckChange,
  onRadioChange,
}: {
  field: AcordPdfField;
  scale: number;
  text: string;
  checked: boolean;
  disabled: boolean;
  onTextChange: (name: string, value: string) => void;
  onCheckChange: (name: string, checked: boolean) => void;
  onRadioChange: (name: string, option: string) => void;
}) {
  const label = acordFieldLabel(field.name);
  const box = {
    left: `${field.left * scale}px`,
    top: `${field.top * scale}px`,
  };
  const isDisabled = disabled || field.readOnly;

  if (field.kind === "checkbox" || field.kind === "radio") {
    const side = Math.max(Math.min(field.width, field.height) * scale, 8);
    const shared = {
      style: { ...box, width: `${side}px`, height: `${side}px` },
      className: "absolute m-0 cursor-pointer accent-primary disabled:cursor-not-allowed",
      disabled: isDisabled,
      "aria-label": label,
      "data-testid": `acord-field-${field.name}${field.option ? `-${field.option}` : ""}`,
    };

    if (field.kind === "radio") {
      return (
        <input
          {...shared}
          type="radio"
          name={field.name}
          value={field.option ?? ""}
          checked={checked}
          onChange={() => field.option && onRadioChange(field.name, field.option)}
        />
      );
    }
    return (
      <input
        {...shared}
        type="checkbox"
        checked={checked}
        onChange={(event) => onCheckChange(field.name, event.target.checked)}
      />
    );
  }

  const shared = {
    value: text,
    onChange: (event: { target: { value: string } }) =>
      onTextChange(field.name, event.target.value),
    // A declared /MaxLen clips server-side. Stopping the agent at the same limit
    // is the difference between a visible boundary and typing that vanishes.
    maxLength: field.maxLength ?? undefined,
    disabled: isDisabled,
    title: label,
    "aria-label": label,
    "data-testid": `acord-field-${field.name}`,
    style: {
      ...box,
      width: `${field.width * scale}px`,
      height: `${field.height * scale}px`,
      // Sized to the blank rather than to the value: the server does the real
      // fitting, so this only has to look like the form.
      fontSize: `${clamp(field.height * scale * 0.66, 6, 14)}px`,
    },
    className:
      "absolute m-0 resize-none overflow-hidden border-0 bg-[#F1B72C]/15 px-[1px] py-0 font-sans leading-tight text-foreground outline-none focus:bg-[#F1B72C]/35 focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-70",
  };

  if (field.kind === "multiline") {
    return <textarea {...shared} />;
  }
  return <input {...shared} type="text" autoComplete="off" />;
});
